import { IAuditTemplateController } from "Interfaces/Controllers/IAuditTemplateController";
import { IEnvConfigUtils } from "Interfaces/Utils/IEnvConfigUtils";
import { inject, injectable } from "inversify";
import { AuditItemTemplate } from "Models/Audit/AuditItemTemplate";
import { AuditSuggestion } from "Models/Audit/AuditSuggestion";
import { AuditTemplate } from "Models/Audit/AuditTemplate";
import BadRequestException from "Models/Exceptions/BadRequestException";
import { SuggestionFilter } from "Models/Filters/SuggestionFilter";
import { TaskTemplateStep } from "Models/Tasks/TaskTemplateStep";
import Types from "ServiceProvider/Types";
import i18n from "Translations/i18n";
import request from "../BaseRequest";
import { BaseTemplateController } from "./BaseTemplateController";

@injectable()
export class AuditTemplateController extends BaseTemplateController<AuditTemplate> implements IAuditTemplateController {
    constructor(@inject(Types.IEnvConfigUtils) envConfigUtils: IEnvConfigUtils) {
        super(envConfigUtils, "audittemplates");
    }

    public async getSuggestionsByFilterAsync(filter: SuggestionFilter): Promise<AuditSuggestion[]> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/suggestions`)
            .query(filter)
            .set(this.headers);

        return response.body as AuditSuggestion[];
    }

    public async getOneSuggestionAsync(auditTemplateId: number): Promise<AuditSuggestion> {
        this.init();

        const response = await request
            .get(`${this.endpoint}/suggestions/${auditTemplateId}`)
            .set(this.headers);

        return response.body as AuditSuggestion;
    }
    
    public async insertOneAsync(auditTemplate: AuditTemplate): Promise<AuditTemplate> {
        this.init();

        const result = await request
            .post(this.endpoint)
            .send(auditTemplate)
            .set(this.headers)
            .then((response: any) => {
                return response.body as AuditTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async updateOneAsync(auditTemplate: AuditTemplate): Promise<AuditTemplate> {
        this.init();

        const result = await request
            .put(`${this.endpoint}/${auditTemplate.id}`)
            .send(auditTemplate)
            .set(this.headers)
            .then((response: any) => {
                return response.body as AuditTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    public async deleteByIdAsync(auditTemplateId: number): Promise<void> {
        this.init();

        await request
            .delete(`${this.endpoint}/${auditTemplateId}`)
            .set(this.headers);
    }

    public async duplicateOneAsync(auditTemplate: AuditTemplate): Promise<AuditTemplate> {
        this.init();

        const result = await request
            .post(`${this.endpoint}/${auditTemplate.id}/duplicate`)
            .send({ duplicatePrefix: i18n.t("common.copyOf") })
            .set(this.headers)
            .then((response: any) => {
                return response.body as AuditTemplate;
            })
            .catch((error: any) => {
                throw this.handleSaveError(error);
            });

        return result;
    }

    private handleSaveError(error: any): Error {
        if (error.response.badRequest) {
            return new BadRequestException(error.response.body.validationErrors);
        }
        else {
            return error;
        }
    }

    public async uploadMediaAsync(auditTemplate: AuditTemplate, video?: File, videoThumbnail?: File, picture?: File, description?: File): Promise<AuditTemplate> {
        this.init();

        const formData = new FormData();
        video && formData.append("video", video, video.name);
        videoThumbnail && formData.append("videoThumbnail", videoThumbnail, videoThumbnail.name);
        picture && formData.append("picture", picture, picture.name);
        description && formData.append("description", description, description.name);

        const response = await request
            .post(`${this.endpoint}/${auditTemplate.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as AuditTemplate;
    }

    public async uploadItemMediaAsync(auditTemplate: AuditTemplate, item: AuditItemTemplate): Promise<AuditItemTemplate> {
        this.init();

        const formData = new FormData();
        item.videoFile && formData.append("video", item.videoFile);
        item.videoThumbnailFile && formData.append("videoThumbnail", item.videoThumbnailFile);
        item.pictureFile && formData.append("picture", item.pictureFile);
        item.descriptionFile && formData.append("description", item.descriptionFile, item.descriptionFileUrl);

        const response = await request
            .post(`${this.endpoint}/${auditTemplate.id}/items/${item.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as AuditItemTemplate;
    }

    public async uploadStepMediaAsync(auditTemplate: AuditTemplate, item: AuditItemTemplate, step: TaskTemplateStep): Promise<TaskTemplateStep> {
        this.init();

        const formData = new FormData();
        step.videoFile && formData.append("video", step.videoFile);
        step.videoThumbnailFile && formData.append("videoThumbnail", step.videoThumbnailFile);
        step.pictureFile && formData.append("picture", step.pictureFile);

        const response = await request
            .post(`${this.endpoint}/${auditTemplate.id}/items/${item.id}/steps/${step.id}/media`)
            .send(formData)
            .set(this.headers);

        return response.body as TaskTemplateStep;
    }
}


// WEBPACK FOOTER //
// ./src/Apis/Controllers/AuditTemplateController.ts